import React, {Fragment, useCallback, useEffect, useState} from 'react';
import {Field} from 'react-final-form';
import t from 'babel-plugin-yate/macro';
import get from 'lodash-es/get';
import {Select} from '../Form/Select';
import {
  composeFormValidators,
  hasMaxLength,
  isAlphaNumeric,
  isRequired,
} from '../../lib/validations';
import {
  formatWhiteSpaceFormField,
  parseNumberFormField,
  filterPostalCodes,
} from '../../lib/misc';
import {Input} from '../Form/Input';
import {Textarea} from '../Form/Textarea';
import {useDispatch, useSelector} from 'react-redux';
import {getCities, getRegions, getPostalCodes} from '../../store/request';

const Address = props => {
  const dispatch = useDispatch();
  const {
    displayFullAddressId = false,
    hasInitialFullAddressId = false,
    postalCodeRequired = false,
    disabled = false,
    defaultValueLabel,
    form,
    uuid,
  } = props;
  const {regions, cities, postalCodes} = useSelector(state => state.request);
  // const [isLoading, setIsLoading] = useState(false);
  const [filteredPostalCodes, setFilteredPostalCodes] = useState(null);

  const addressObjExtracted = get(
    form.getState().values,
    `customer.${defaultValueLabel}`,
  );
  const {region_id, locality_id, street} = addressObjExtracted || {};

  const resetAddressFields = useCallback(
    fields => {
      fields.forEach(key => {
        form.change(`customer.${defaultValueLabel}.${key}`, null);
      });
    },
    [defaultValueLabel, form],
  );

  useEffect(() => {
    !regions && dispatch(getRegions(uuid));
  }, [dispatch, regions, uuid]);

  useEffect(() => {
    if (!region_id || cities?.[region_id]) {
      return;
    }

    dispatch(getCities(region_id, uuid));
  }, [dispatch, region_id, cities, uuid]);

  useEffect(() => {
    if (!postalCodeRequired || !locality_id || postalCodes?.[locality_id]) {
      return;
    }

    dispatch(getPostalCodes(locality_id, uuid));
  }, [dispatch, locality_id, postalCodes, uuid, postalCodeRequired]);

  useEffect(() => {
    if (!postalCodeRequired || !street || !postalCodes || !locality_id) {
      return;
    }

    filterPostalCodes(postalCodes[locality_id], street, setFilteredPostalCodes);
  }, [postalCodes, locality_id, street, postalCodeRequired]);

  if (!regions) {
    return [];
  }

  return (
    <Fragment>
      <div className="md:flex md:flex-wrap">
        <div className="w-full md:w-1/2 px-2 mb-4">
          <Field
            name={`customer.${defaultValueLabel}.region_id`}
            label={t`State`}
            component={Select}
            disabled={disabled}
            validate={isRequired()}
            parse={value => {
              resetAddressFields(['locality_id', 'postal_code']);

              return parseNumberFormField(value);
            }}>
            <option value="" disabled>{t`Choose a region`}</option>
            {regions?.map(({id, name}) => (
              <option key={`region-select-${id}`} value={id}>
                {name}
              </option>
            ))}
          </Field>
        </div>

        <div className="w-full md:w-1/2 px-2 mb-4">
          <Field
            name={`customer.${defaultValueLabel}.locality_id`}
            label={t`City`}
            component={Select}
            parse={value => {
              resetAddressFields(['postal_code']);

              return parseNumberFormField(value);
            }}
            validate={isRequired()}
            disabled={disabled}>
            {region_id && cities[region_id] ? (
              <>
                <option value="" disabled>{t`Choose a city`}</option>

                {region_id &&
                  cities[region_id]?.map(({id, name}) => (
                    <option key={`city-select-${id}`} value={id}>
                      {name}
                    </option>
                  ))}
              </>
            ) : (
              <option disabled value="">
                {t`Select a region first`}
              </option>
            )}
          </Field>
        </div>

        {hasInitialFullAddressId || displayFullAddressId ? (
          <div className="w-full px-2 mb-4">
            <Field
              name={`customer.${defaultValueLabel}.full_address`}
              label={t`ID address`}
              component={Textarea}
              validate={isRequired()}
              disabled={hasInitialFullAddressId}
            />
          </div>
        ) : (
          <div className="flex flex-wrap">
            <div className="w-full sm:w-3/4 px-2 mb-4">
              <Field
                name={`customer.${defaultValueLabel}.street`}
                label={t`Street`}
                component={Input}
                disabled={disabled}
                validate={composeFormValidators(
                  isRequired(),
                  isAlphaNumeric(),
                  hasMaxLength(255),
                )}
              />
            </div>

            <div className="w-full sm:w-1/4 px-2 mb-4">
              <Field
                name={`customer.${defaultValueLabel}.street_number`}
                label={t`Number`}
                component={Input}
                disabled={disabled}
                format={formatWhiteSpaceFormField}
                validate={composeFormValidators(
                  isRequired(),
                  isAlphaNumeric(),
                  hasMaxLength(255),
                )}
              />
            </div>

            {postalCodeRequired && (
              <div className="w-full px-2 mb-4">
                <Field
                  name={`customer.${defaultValueLabel}.postal_code`}
                  label={t`Cod postal`}
                  component={Select}
                  disabled={disabled || !street || !locality_id}
                  // loadingData={isLoading}
                  validate={isRequired()}>
                  <option disabled value="">{t`Alege un cod postal`}</option>
                  {filteredPostalCodes?.map(({id, code, name}) => (
                    <option key={`postal-code-select-${id}`} value={code}>
                      {name}
                    </option>
                  ))}
                </Field>
              </div>
            )}

            <div className="w-1/2 sm:w-1/4 px-2 mb-4">
              <Field
                name={`customer.${defaultValueLabel}.building`}
                label={t`Building`}
                component={Input}
                disabled={disabled}
                format={formatWhiteSpaceFormField}
                validate={composeFormValidators(
                  isAlphaNumeric(),
                  hasMaxLength(255),
                )}
              />
            </div>

            <div className="w-1/2 sm:w-1/4 px-2 mb-4">
              <Field
                name={`customer.${defaultValueLabel}.staircase`}
                label={t`Entrance`}
                component={Input}
                disabled={disabled}
                format={formatWhiteSpaceFormField}
                validate={composeFormValidators(
                  isAlphaNumeric(),
                  hasMaxLength(255),
                )}
              />
            </div>

            <div className="w-1/2 sm:w-1/4 px-2 mb-4">
              <Field
                name={`customer.${defaultValueLabel}.floor`}
                label={t`Floor`}
                component={Input}
                disabled={disabled}
                format={formatWhiteSpaceFormField}
                validate={composeFormValidators(isAlphaNumeric())}
              />
            </div>

            <div className="w-1/2 sm:w-1/4 px-2 mb-4">
              <Field
                name={`customer.${defaultValueLabel}.apartment_number`}
                label={t`Apt`}
                component={Input}
                disabled={disabled}
                format={formatWhiteSpaceFormField}
                validate={composeFormValidators(
                  isAlphaNumeric(),
                  hasMaxLength(255),
                )}
              />
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default Address;
